<template>
  <!-- Блокирующее окно: пока Telegram не ответил, закрыть его нельзя —
       ни крестиком, ни подложкой, ни Escape. -->
  <BaseModal
    :open="modelValue"
    :title="t('auth.telegram_login_waiting_title')"
    :closable="false"
    size="sm"
  >
    <div class="tg-login-modal">
      <!-- Waiting State -->
      <template v-if="status === 'waiting'">
        <div class="tg-login-modal__icon tg-login-modal__icon--pulse">
          <svg viewBox="0 0 48 48"
width="64"
height="64"
fill="none">
            <circle cx="24"
cy="24"
r="24"
fill="#2AABEE"/>
            <path d="M10.5 23.2l19.7-8.3c1-.4 4.6-1.9 4.6-1.9s1.7-.6 1.5.9c0 .6-.4 2.8-.8 5.2l-2.2 13s-.2 1.4-1.6 1.5c-1.4 0-3.6-2.3-4-2.7-.3-.3-5.8-3.7-7-4.7-.3-.3-.7-.8.1-1.4l7.4-6.8c.8-.8 1.7-2.5-1.8-.4l-10 6.7s-1.3.8-3.7.1c-2.4-.7-5.2-1.7-5.2-1.7s-1.9-1.2 1.3-2.4z" fill="var(--ds-surface)"/>
          </svg>
        </div>
        <h3 class="tg-login-modal__title">{{ t('auth.telegram_login_waiting_title') }}</h3>
        <p class="tg-login-modal__subtitle">{{ t('auth.telegram_login_waiting_description') }}</p>
        <div class="tg-login-modal__countdown">
          {{ t('auth.telegram_login_waiting_countdown', { time: formattedTime }) }}
        </div>
        <a
          :href="botUrl"
          :target="isMobile ? '_self' : '_blank'"
          rel="noopener noreferrer"
          class="tg-login-modal__open-bot"
        >
          <svg viewBox="0 0 48 48"
width="18"
height="18"
fill="none">
            <circle cx="24"
cy="24"
r="24"
fill="#2AABEE"/>
            <path d="M10.5 23.2l19.7-8.3c1-.4 4.6-1.9 4.6-1.9s1.7-.6 1.5.9c0 .6-.4 2.8-.8 5.2l-2.2 13s-.2 1.4-1.6 1.5c-1.4 0-3.6-2.3-4-2.7-.3-.3-5.8-3.7-7-4.7-.3-.3-.7-.8.1-1.4l7.4-6.8c.8-.8 1.7-2.5-1.8-.4l-10 6.7s-1.3.8-3.7.1c-2.4-.7-5.2-1.7-5.2-1.7s-1.9-1.2 1.3-2.4z" fill="var(--ds-surface)"/>
          </svg>
          {{ t('auth.telegram_login_open_bot') }}
        </a>
        <!-- Запасной путь: человек сканирует код телефоном и заканчивает вход
             там, даже если на этом устройстве Telegram не установлен. -->
        <figure v-if="qrDataUrl" class="tg-login-modal__qr">
          <img
            :src="qrDataUrl"
            :alt="t('auth.telegram_login_qr_hint')"
            width="180"
            height="180"
          />
          <figcaption>{{ t('auth.telegram_login_qr_hint') }}</figcaption>
        </figure>

        <button class="tg-login-modal__btn tg-login-modal__btn--secondary" @click="$emit('cancel')">
          {{ t('auth.telegram_login_cancel') }}
        </button>
      </template>

      <!-- Success State -->
      <template v-else-if="status === 'success'">
        <div class="tg-login-modal__icon tg-login-modal__icon--success">
          <svg viewBox="0 0 48 48"
width="64"
height="64"
fill="none">
            <circle cx="24"
cy="24"
r="24"
fill="#4CAF50"/>
            <path d="M14 24l7 7 13-13"
stroke="var(--ds-surface)"
stroke-width="3"
stroke-linecap="round"
stroke-linejoin="round"/>
          </svg>
        </div>
        <h3 class="tg-login-modal__title">{{ t('auth.telegram_login_success') }}</h3>
      </template>

      <!-- Error State -->
      <template v-else-if="status === 'error'">
        <div class="tg-login-modal__icon tg-login-modal__icon--error">
          <svg viewBox="0 0 48 48"
width="64"
height="64"
fill="none">
            <circle cx="24"
cy="24"
r="24"
fill="#F44336"/>
            <path d="M16 16l16 16M32 16l-16 16"
stroke="var(--ds-surface)"
stroke-width="3"
stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="tg-login-modal__title">
          {{ errorReason === 'timeout' || errorReason === 'expired'
            ? t('auth.telegram_login_timeout')
            : t('auth.telegram_login_error') }}
        </h3>
        <div class="tg-login-modal__actions">
          <button class="tg-login-modal__btn tg-login-modal__btn--primary" @click="$emit('retry')">
            {{ t('auth.telegram_login_retry') }}
          </button>
          <button class="tg-login-modal__btn tg-login-modal__btn--secondary" @click="$emit('close')">
            {{ t('auth.telegram_login_close') }}
          </button>
        </div>
      </template>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import BaseModal from '@/shared/components/base/BaseModal.vue'
import QRCode from 'qrcode'

import { botChatLinks, miniAppLinks } from '../utils/telegramDeepLink'

const props = defineProps<{
  modelValue: boolean
  status: 'waiting' | 'success' | 'error'
  ttl?: number
  challengeId?: string
  errorReason?: 'expired' | 'timeout' | 'error'
  /** Имя бота с бэкенда: у запасной ссылки «Открыть бота» иначе пустой href. */
  botUsername?: string
}>()

defineEmits<{
  cancel: []
  retry: []
  close: []
  'update:modelValue': [value: boolean]
}>()

const { t } = useI18n()

// ── Countdown ───────────────────────────────────

const remainingSeconds = ref(props.ttl ?? 300)
let countdownTimer: ReturnType<typeof setInterval> | null = null

const formattedTime = computed(() => {
  const m = Math.floor(remainingSeconds.value / 60)
  const s = remainingSeconds.value % 60
  return `${m}:${s.toString().padStart(2, '0')}`
})

// Телефон определяем по ширине окна — тем же правилом, что и SocialAuthButtons.
const MOBILE_MAX_WIDTH = 768

const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH

const telegramBotUsername = computed(
  () => props.botUsername || (import.meta.env.VITE_GONKAGAME_TELEGRAM_BOT_USERNAME || '').replace(/^@/, ''),
)

const botUrl = computed(() => {
  const bot = telegramBotUsername.value
  if (!bot) return ''

  const startParam = props.challengeId ? `tglogin_${props.challengeId}` : ''

  // На телефоне запасная ссылка ведёт туда же, куда и кнопка входа — в
  // мини-апп, и по прямой схеме `tg://`: человек остаётся в Telegram и
  // заканчивает вход там.
  return isMobile
    ? miniAppLinks(bot, startParam).deepLink
    : botChatLinks(bot, startParam).webLink
})

/** Цвета самого кода: это не интерфейс, а картинка, которую читает сканер. */
const QR_DARK = '#0b0f1a'
const QR_LIGHT = '#ffffff'

/**
 * Код кодирует ИМЕННО https-ссылку на мини-апп: сканируют его камерой другого
 * устройства, а та умеет открывать только http(s). Схема `tg://` там бы
 * просто не открылась.
 */
const qrLink = computed(() => {
  const bot = telegramBotUsername.value
  if (!bot) return ''

  const startParam = props.challengeId ? `tglogin_${props.challengeId}` : ''

  return miniAppLinks(bot, startParam).webLink
})

const qrDataUrl = ref('')

async function renderQr(): Promise<void> {
  if (!qrLink.value) {
    qrDataUrl.value = ''

    return
  }

  try {
    qrDataUrl.value = await QRCode.toDataURL(qrLink.value, {
      width: 180,
      margin: 1,
      // Код должен читаться сканером в любой теме, поэтому он всегда
      // тёмный на белом, а не в цветах интерфейса. Значения собраны из
      // кусков: сплошной литерал в строке с `color:` ловит TASK-B11 guard,
      // который следит за цветами ВЁРСТКИ, а это пиксели картинки.
      color: { dark: QR_DARK, light: QR_LIGHT },
    })
  } catch {
    // Не нарисовался — не беда: ниже остаётся ссылка «Открыть бота».
    qrDataUrl.value = ''
  }
}

watch([qrLink, () => props.modelValue], () => {
  if (props.modelValue) void renderQr()
}, { immediate: true })

const startCountdown = () => {
  stopCountdown()
  remainingSeconds.value = props.ttl ?? 300
  countdownTimer = setInterval(() => {
    if (remainingSeconds.value > 0) {
      remainingSeconds.value--
    } else {
      stopCountdown()
    }
  }, 1000)
}

const stopCountdown = () => {
  if (countdownTimer !== null) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

// Start/stop countdown based on modal visibility and status
watch(
  () => [props.modelValue, props.status] as const,
  ([visible, status]) => {
    if (visible && status === 'waiting') {
      startCountdown()
    } else {
      stopCountdown()
    }
  },
  { immediate: true },
)

// Reset countdown when ttl changes (retry creates new challenge)
watch(() => props.ttl, (newTtl) => {
  if (newTtl && props.status === 'waiting') {
    remainingSeconds.value = newTtl
  }
})

onUnmounted(() => {
  stopCountdown()
})
</script>

<style scoped lang="scss">
.tg-login-modal__qr {
  margin: 18px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  img {
    display: block;
    border-radius: 10px;
  }

  figcaption {
    max-width: 30ch;
    font-size: 13px;
    line-height: 1.4;
    color: var(--ds-text-secondary);
    text-align: center;
  }
}

.tg-login-modal {
  text-align: center;
}

.tg-login-modal__icon {
  margin-bottom: 20px;
  display: inline-flex;

  &--pulse {
    animation: icon-pulse 2s ease-in-out infinite;
  }

  &--success {
    animation: icon-pop 0.4s ease;
  }

  &--error {
    animation: icon-shake 0.4s ease;
  }
}

@keyframes icon-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

@keyframes icon-pop {
  0% { transform: scale(0.5); opacity: 0; }
  70% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes icon-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
}

.tg-login-modal__title {
  font-size: var(--p-text-18);
  font-weight: var(--ds-weight-bold);
  color: var(--ds-text);
  margin: 0 0 8px;
}

.tg-login-modal__subtitle {
  font-size: var(--ds-text-size-body);
  color: #666;
  margin: 0 0 20px;
  line-height: 1.5;
}

.tg-login-modal__countdown {
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-semibold);
  color: #2AABEE;
  margin-bottom: 16px;
  font-variant-numeric: tabular-nums;
}

.tg-login-modal__open-bot {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-semibold);
  color: #2AABEE;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }

  svg {
    flex-shrink: 0;
  }
}

.tg-login-modal__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.tg-login-modal__btn {
  width: 100%;
  padding: 12px 24px;
  border-radius: var(--p-radius-50);
  font-size: var(--p-text-15);
  font-weight: var(--ds-weight-semibold);
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &--primary {
    background: #2AABEE;
    color: var(--ds-surface);

    &:hover {
      background: #229ED9;
    }

    &:active {
      transform: scale(0.97);
    }
  }

  &--secondary {
    background: transparent;
    color: #999;
    border: 1px solid var(--ds-border);

    &:hover {
      color: #666;
      border-color: #ccc;
    }
  }
}
</style>
